import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const Splash = () => {
    const navigate = useNavigate();
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [isStandalone, setIsStandalone] = useState(false);

    useEffect(() => {
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        const isStandaloneMode = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;

        // 1. If Desktop OR Already Installed -> Go to App
        if (!isMobile || isStandaloneMode) {
            setIsStandalone(true);
            const target = localStorage.getItem("winrox11_token") ? "/home" : "/login";
            setTimeout(() => navigate(target), 500);
            return;
        }

        // 2. Capture Install Prompt (Only for Mobile)
        const handler = (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
        };

        window.addEventListener('beforeinstallprompt', handler);
        return () => window.removeEventListener('beforeinstallprompt', handler);
    }, [navigate]);

    const handleInstall = async () => {
        if (!deferredPrompt) {
            // Already installed or not supported, go to login
            navigate("/login");
            return;
        }
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        if (outcome === 'accepted') {
            setDeferredPrompt(null);
            // Auto redirect to login after install
            navigate("/login");
        }
    };

    // Listen for app installed event - auto open the app
    useEffect(() => {
        const handleAppInstalled = () => {
            console.log("App installed! Redirecting...");
            const target = localStorage.getItem("winrox11_token") ? "/home" : "/login";
            navigate(target);
        };
        window.addEventListener('appinstalled', handleAppInstalled);
        return () => window.removeEventListener('appinstalled', handleAppInstalled);
    }, [navigate]);

    if (isStandalone) {
        return (
            <div className="bg-theme-bg min-h-screen flex flex-col items-center justify-center p-6 text-center">
                <div className="relative mb-10">
                  <div className="absolute inset-0 bg-accent/20 blur-[60px] rounded-full scale-150 animate-pulse"></div>
                  <img src="/cricket_logo.png" alt="WinroX11" className="w-32 h-32 rounded-[28%] relative z-10 shadow-theme-lg" />
                </div>
                <h1 className="text-4xl font-bold text-theme-text tracking-tighter uppercase mb-2">WinroX11</h1>
                <p className="text-[10px] font-bold text-theme-text-sec uppercase tracking-[0.4em] opacity-40">Fantasy Sports Engine</p>
                <div className="mt-20 w-10 h-10 border-2 border-accent border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    return (
        <div className="bg-theme-bg min-h-screen flex flex-col font-sans text-theme-text relative overflow-hidden">
            {/* Background Decoration */}
            <div className="absolute top-[-10%] right-[-10%] w-[80%] h-[40%] bg-accent/5 rounded-full blur-[120px]"></div>
            <div className="absolute bottom-[-10%] left-[-10%] w-[60%] h-[30%] bg-accent/5 rounded-full blur-[100px]"></div>

            <div className="flex-1 flex flex-col items-center justify-center p-10 text-center relative z-10">
                <div className="relative mb-12">
                   <div className="absolute inset-0 bg-accent/10 blur-[80px] rounded-full scale-125"></div>
                   <img src="/cricket_logo.png" alt="WinroX11 Logo" className="w-40 h-40 rounded-[30%] relative z-10 shadow-theme-lg border-4 border-theme-card" />
                </div>
                
                <h1 className="text-5xl font-bold tracking-tighter text-theme-text mb-4 uppercase">Winro<span className="text-accent underline decoration-4 underline-offset-8">X11</span></h1>
                <p className="text-theme-text-sec text-xs font-bold uppercase tracking-[0.2em] mb-12 opacity-40">Play. Predict. Win Cash.</p>
                
                <div className="flex flex-col gap-4 w-full max-w-[280px]">
                  <div className="bg-theme-card p-6 rounded-[32px] border border-theme-border shadow-theme-sm mb-4">
                     <div className="flex justify-around mb-6">
                        <div className="text-center">
                           <p className="text-lg font-bold text-theme-text">4.8★</p>
                           <p className="text-[8px] font-bold text-theme-text-sec uppercase tracking-widest opacity-40">Rating</p>
                        </div>
                         <div className="w-px h-8 bg-theme-border opacity-50"></div>
                        <div className="text-center">
                           <p className="text-lg font-bold text-theme-text">10M+</p>
                           <p className="text-[8px] font-bold text-theme-text-sec uppercase tracking-widest opacity-40">Downloads</p>
                        </div>
                     </div>
                     <p className="text-[10px] text-theme-text font-medium leading-relaxed opacity-60">Join India's most trusted and fastest growing fantasy platform.</p>
                  </div>

                  <button 
                      onClick={handleInstall}
                      className="w-full bg-accent text-white py-5 rounded-[24px] font-bold text-xs uppercase tracking-[0.2em] shadow-theme-lg transition active:scale-95 flex items-center justify-center gap-3"
                  >
                      {deferredPrompt ? (
                        <>
                           <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/></svg>
                           Install App
                        </>
                      ) : "Get Started"}
                  </button>
                  
                  {!deferredPrompt && (
                     <button onClick={() => navigate("/login")} className="text-[10px] font-bold text-theme-text-sec uppercase tracking-[0.2em] opacity-40 py-2">Continue to Login</button>
                  )}
                </div>
            </div>

            <div className="p-8 text-center bg-theme-card border-t border-theme-border">
                <p className="text-[9px] font-bold text-theme-text-sec uppercase tracking-widest opacity-40">100% SECURE & VERIFIED</p>
            </div>
            <style dangerouslySetInnerHTML={{ __html: `
                .scrollbar-hide::-webkit-scrollbar { display: none; }
                .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
            `}} />
        </div>
    );
};

export default Splash;
